import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, MessageCircle, Send, Eye, EyeOff, Users, Save, Bell, IdCard as IdCardIcon, KeyRound, AtSign, Power, RefreshCcw, Mail, Copy, Globe, Facebook, Instagram, Tags } from "@/lib/icons";
import IdCard from "@/components/IdCard";
import PhotoUpload from "@/components/PhotoUpload";
import CategoryMultiSelect, { selectedCategories } from "@/components/CategoryMultiSelect";
import { useSettings } from "@/lib/settings";
import { withEmailDeliveryNote } from "@/components/EmailDeliveryNotice";

const VIS_OPTIONS = [
  { value: "public", label: "Public", icon: Eye, tone: "text-emerald-400" },
  { value: "members", label: "Members-only", icon: Users, tone: "text-amber-300" },
  { value: "hidden", label: "Hidden", icon: EyeOff, tone: "text-red-400" },
];
const PREFERRED = [
  { value: "phone", label: "Phone Call" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "telegram", label: "Telegram" },
  { value: "any", label: "Any Method" },
];
const inputCls = "h-10 bg-white/5 border-white/10 text-white placeholder:text-white/35";

export default function MemberDetailSheet({ memberId, open, onOpenChange, onSaved }) {
  const { settings } = useSettings();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!open || !memberId) return;
    setM(null);
    api.get(`/admin/members/${encodeURIComponent(memberId)}`).then((r) => {
      const found = r.data;
      if (found) setM({
        ...found,
        categories: selectedCategories(found),
        visibility: { mobile: "public", whatsapp: "public", telegram: "public", ...(found.visibility || {}) },
        preferred_contact_method: found.preferred_contact_method || "any",
      });
    }).catch((e) => {
      toast.error(formatError(e));
      onOpenChange(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, open]);

  const setVis = (k, v) => setM((s) => ({ ...s, visibility: { ...s.visibility, [k]: v } }));
  const setField = (k, v) => setM((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/members/${encodeURIComponent(memberId)}`, {
        category: m.category || "",
        categories: m.categories || [],
        website: m.website || "",
        facebook_url: m.facebook_url || "",
        instagram_url: m.instagram_url || "",
        whatsapp_number: m.whatsapp_number || "",
        telegram_username: m.telegram_username || "",
        telegram_number: m.telegram_number || "",
        preferred_contact_method: m.preferred_contact_method,
        visibility: m.visibility,
      });
      toast.success("Member updated");
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  const resendNotification = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/admin/members/${encodeURIComponent(memberId)}/resend-notification`);
      if (data.email_configured) {
        toast.success(withEmailDeliveryNote("Notification emails sent."));
      } else {
        toast.success("Notification queued. The email provider is not configured, so it was logged by the backend.");
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setResending(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto bg-ink-900 border-l border-white/10 text-white" data-testid="member-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-3xl text-white">Member details</SheetTitle>
          <SheetDescription className="text-white/55">View and control what's visible on the public profile.</SheetDescription>
        </SheetHeader>

        {!m ? (
          <div className="py-16 text-center text-white/40">Loading…</div>
        ) : (
          <div className="mt-6 space-y-6">
            <div className="glass rounded-2xl p-5">
              <div className="overline text-fuchsia-300/80 text-[10px]">{m.member_id}</div>
              <div className="mt-1 font-display text-2xl text-white">{m.full_name}</div>
              {m.studio_name && <div className="text-sm text-white/60">{m.studio_name}</div>}
              <div className="mt-2 text-xs text-white/50">
                Status: <span className="font-semibold text-white capitalize">{m.status}</span>
                {" · "}Source: <span className="font-semibold capitalize">{m.source || (m.imported ? "imported" : "manual")}</span>
              </div>
            </div>

            <div className="glass rounded-2xl p-5" data-testid="admin-member-review-profile">
              <div className="flex items-center gap-2 font-display text-xl text-white">
                <Tags size={18} className="text-fuchsia-300" /> Categories & public links
              </div>
              <p className="mt-1 text-xs text-white/50">Review the member's specialties and social profiles before approving them.</p>
              <div className="mt-4">
                <label className="overline text-white/60 text-[10px]">Categories</label>
                <div className="mt-2">
                  <CategoryMultiSelect
                    options={(settings?.categories || []).filter((item) => item && item.enabled !== false && item.name)}
                    categories={m.categories || []}
                    primary={m.category || ""}
                    onChange={({ categories, category }) => setM((value) => ({ ...value, categories, category }))}
                    testid="admin-member-categories"
                  />
                </div>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <SocialField icon={Globe} label="Website" value={m.website} onChange={(value) => setField("website", value)} testid="detail-website" />
                <SocialField icon={Facebook} label="Facebook" value={m.facebook_url} onChange={(value) => setField("facebook_url", value)} testid="detail-facebook" />
                <SocialField icon={Instagram} label="Instagram" value={m.instagram_url} onChange={(value) => setField("instagram_url", value)} testid="detail-instagram" />
              </div>
            </div>

            <div className="glass rounded-2xl p-5">
              <div className="overline text-white/50 mb-3">Profile photo</div>
              <PhotoUpload
                src={m.photo_url}
                endpoint={`/admin/members/${encodeURIComponent(memberId)}/photo`}
                deleteEndpoint={`/admin/members/${encodeURIComponent(memberId)}/photo`}
                testidPrefix="admin-photo"
                size="md"
                name={m.full_name}
                avatarKey={m.avatar_key}
                onChange={(url) => setM((s) => ({ ...s, photo_url: url, photo_path: url ? s.photo_path : null }))}
              />
            </div>

            <div className="space-y-4">
              <div className="font-display text-xl text-white">Contact information</div>

              <ContactRow icon={Phone} label="Primary Mobile" tone="text-fuchsia-300" required>
                <Input value={m.mobile || ""} disabled className={`${inputCls} opacity-70`} data-testid="detail-mobile" />
                <VisibilitySelect value={m.visibility.mobile} onChange={(v) => setVis("mobile", v)} testid="vis-mobile" />
              </ContactRow>

              <ContactRow icon={MessageCircle} label="WhatsApp Number" tone="text-emerald-400">
                <Input value={m.whatsapp_number || ""} onChange={(e) => setField("whatsapp_number", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="Not provided" className={inputCls} data-testid="detail-whatsapp" />
                <VisibilitySelect value={m.visibility.whatsapp} onChange={(v) => setVis("whatsapp", v)} testid="vis-whatsapp" />
              </ContactRow>

              <ContactRow icon={Send} label="Telegram Username" tone="text-sky-400">
                <Input value={m.telegram_username || ""} onChange={(e) => setField("telegram_username", e.target.value)}
                  placeholder="@handle" className={inputCls} data-testid="detail-telegram-username" />
                <VisibilitySelect value={m.visibility.telegram} onChange={(v) => setVis("telegram", v)} testid="vis-telegram" />
              </ContactRow>

              <ContactRow icon={Send} label="Telegram Phone (optional)" tone="text-sky-400">
                <Input value={m.telegram_number || ""} onChange={(e) => setField("telegram_number", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit number" className={inputCls} data-testid="detail-telegram-number" />
                <div className="w-40" />
              </ContactRow>

              <div>
                <label className="overline text-white/60 text-[10px]">Preferred contact method</label>
                <Select value={m.preferred_contact_method} onValueChange={(v) => setField("preferred_contact_method", v)}>
                  <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="detail-preferred"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PREFERRED.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Digital ID preview */}
            <div className="pt-4 border-t border-white/10">
              <div className="font-display text-xl text-white flex items-center gap-2">
                <IdCardIcon size={18} className="text-fuchsia-300" /> PRONET Digital ID
              </div>
              <div className="text-sm text-white/50 mt-1">
                {m.status === "approved"
                  ? "Card is live. Members and admins can download or print it."
                  : "Approve this member to activate their digital ID card."}
              </div>
              <div className="mt-4"><IdCard member={m} /></div>
            </div>

            <LoginSecurityPanel m={m} setM={setM} />

            <div className="pt-2 flex flex-wrap justify-between gap-2 border-t border-white/10">
              <button onClick={resendNotification} disabled={resending} data-testid="resend-notification"
                className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">
                <Bell size={14} /> {resending ? "Sending…" : "Resend notification"}
              </button>
              <div className="flex gap-2">
                <button onClick={() => onOpenChange(false)}
                  className="btn-ghost rounded-full px-5 py-2 text-sm font-semibold text-white">Cancel</button>
                <button onClick={save} disabled={busy} data-testid="detail-save"
                  className="btn-glow inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">
                  <Save size={14} /> {busy ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function SocialField({ icon: Icon, label, value, onChange, testid }) {
  const href = value ? (value.startsWith("http") ? value : `https://${value}`) : "";
  return (
    <div>
      <label className="overline flex items-center gap-1.5 text-[10px] text-white/60"><Icon size={12} className="text-fuchsia-300" /> {label}</label>
      <Input value={value || ""} onChange={(event) => onChange(event.target.value)} placeholder="Not provided" className={`mt-2 ${inputCls}`} data-testid={testid} />
      {href && <a href={href} target="_blank" rel="noreferrer" className="mt-1.5 inline-block text-[10px] font-semibold text-violet-300 hover:underline">Open {label}</a>}
    </div>
  );
}

function ContactRow({ icon: Icon, label, tone, required, children }) {
  const [input, vis] = React.Children.toArray(children);
  return (
    <div>
      <label className="overline text-white/60 text-[10px] flex items-center gap-1">
        <Icon size={12} className={tone} /> {label} {required && <span className="text-fuchsia-400">*</span>}
      </label>
      <div className="mt-2 flex gap-2 items-center">
        <div className="flex-1">{input}</div>
        <div className="w-44 shrink-0">{vis}</div>
      </div>
    </div>
  );
}

function VisibilitySelect({ value, onChange, testid }) {
  const opt = VIS_OPTIONS.find((o) => o.value === value) || VIS_OPTIONS[0];
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10 bg-white/5 border-white/10 text-white" data-testid={testid}>
        <div className="flex items-center gap-1.5">
          <opt.icon size={13} className={opt.tone} />
          <span className="text-xs">{opt.label}</span>
        </div>
      </SelectTrigger>
      <SelectContent>
        {VIS_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            <div className="flex items-center gap-2"><o.icon size={13} className={o.tone} /> {o.label}</div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function LoginSecurityPanel({ m, setM }) {
  const [tempPw, setTempPw] = useState("");
  const [editingUsername, setEditingUsername] = useState(false);
  const [uname, setUname] = useState(m.username || "");
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState(m.email || "");
  const [busy, setBusy] = useState("");
  const mid = m.member_id;
  const active = m.active !== false;

  const call = async (label, fn) => {
    setBusy(label);
    try { await fn(); }
    catch (e) { toast.error(formatError(e)); }
    finally { setBusy(""); }
  };

  const saveUsername = async () => call("uname", async () => {
    const { data } = await api.patch(`/admin/members/${encodeURIComponent(mid)}/username`, { username: uname.trim().toLowerCase() });
    setM((s) => ({ ...s, username: data.username }));
    setEditingUsername(false);
    toast.success("Username updated");
  });
  const saveEmail = async () => call("email", async () => {
    const { data } = await api.patch(`/admin/members/${encodeURIComponent(mid)}/email`, { email: newEmail.trim().toLowerCase() });
    setM((s) => ({ ...s, email: data.email }));
    setEditingEmail(false);
    toast.success("Email updated");
  });
  const resetPw = async () => call("reset", async () => {
    if (!window.confirm("Generate a new temporary password? The member will be required to change it on next login.")) return;
    const { data } = await api.post(`/admin/members/${encodeURIComponent(mid)}/reset-password`);
    setTempPw(data.temp_password);
    setM((s) => ({ ...s, must_change_password: true }));
    toast.success("New temporary password generated");
  });
  const sendDetails = async () => call("send", async () => {
    const { data } = await api.post(`/admin/members/${encodeURIComponent(mid)}/send-login-details`);
    toast.success(data.email_sent
      ? withEmailDeliveryNote("Login details emailed. The member will set their own password via Forgot Password.")
      : "Email delivery is pending. Please retry shortly.");
  });
  const toggleForce = async () => call("force", async () => {
    const next = !m.must_change_password;
    await api.post(`/admin/members/${encodeURIComponent(mid)}/force-password-change`, { force: next });
    setM((s) => ({ ...s, must_change_password: next }));
    toast.success(next ? "Force-change enabled" : "Force-change cleared");
  });
  const toggleActive = async () => call("active", async () => {
    const endpoint = active ? "deactivate" : "activate";
    await api.post(`/admin/members/${encodeURIComponent(mid)}/${endpoint}`);
    setM((s) => ({ ...s, active: !active }));
    toast.success(active ? "Account deactivated" : "Account activated");
  });
  const copyTemp = async () => {
    await navigator.clipboard.writeText(`${m.username}\t${tempPw}`);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="pt-4 border-t border-white/10" data-testid="admin-login-security">
      <div className="font-display text-xl text-white flex items-center gap-2">
        <KeyRound size={18} className="text-fuchsia-300" /> Login & security
      </div>
      <p className="text-xs text-white/50 mt-1">
        Manage the member's login credentials. The current password cannot be viewed because it is stored securely.
      </p>

      <div className="mt-5 space-y-4">
        {/* Username */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[110px] overline text-white/50 text-[10px]">Username</div>
          {editingUsername ? (
            <>
              <Input value={uname} onChange={(e) => setUname(e.target.value)}
                data-testid="admin-username-input" className={`${inputCls} flex-1 min-w-[180px]`} />
              <button onClick={saveUsername} disabled={busy === "uname"} data-testid="admin-username-save"
                className="btn-glow rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">Save</button>
              <button onClick={() => { setEditingUsername(false); setUname(m.username || ""); }}
                className="text-xs text-white/40 hover:text-white/70 px-2">Cancel</button>
            </>
          ) : (
            <>
              <span className="font-mono text-sm text-fuchsia-200 flex-1 min-w-[180px]" data-testid="admin-username-value">{m.username || "Not set"}</span>
              <button onClick={() => setEditingUsername(true)} data-testid="admin-username-edit"
                className="btn-ghost inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white">Edit</button>
            </>
          )}
        </div>

        {/* Email */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[110px] overline text-white/50 text-[10px]">Email</div>
          {editingEmail ? (
            <>
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                data-testid="admin-email-input" className={`${inputCls} flex-1 min-w-[220px]`} />
              <button onClick={saveEmail} disabled={busy === "email"} data-testid="admin-email-save"
                className="btn-glow rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">Save</button>
              <button onClick={() => { setEditingEmail(false); setNewEmail(m.email || ""); }}
                className="text-xs text-white/40 hover:text-white/70 px-2">Cancel</button>
            </>
          ) : (
            <>
              <span className="text-sm text-white/80 flex-1 min-w-[220px] break-all" data-testid="admin-email-value">{m.email || "Not set"}</span>
              <button onClick={() => setEditingEmail(true)} data-testid="admin-email-edit"
                className="btn-ghost inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white">
                <AtSign size={11} /> Change
              </button>
            </>
          )}
        </div>

        {/* Force password change + active toggle */}
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={toggleForce} disabled={busy === "force"} data-testid="admin-force-toggle"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold border transition-colors ${
              m.must_change_password
                ? "bg-amber-500/15 border-amber-400/40 text-amber-200"
                : "bg-white/5 border-white/15 text-white/70 hover:text-white"
            }`}>
            <RefreshCcw size={12} /> Force pw change: {m.must_change_password ? "ON" : "off"}
          </button>
          <button onClick={toggleActive} disabled={busy === "active"} data-testid="admin-active-toggle"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold border transition-colors ${
              active
                ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200"
                : "bg-red-500/15 border-red-400/40 text-red-200"
            }`}>
            <Power size={12} /> {active ? "Active" : "Deactivated"}
          </button>
        </div>

        {/* Reset password + send login details */}
        <div className="flex flex-wrap gap-2 pt-2">
          <button onClick={resetPw} disabled={busy === "reset"} data-testid="admin-reset-pw"
            className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white">
            <KeyRound size={12} /> Reset password
          </button>
          <button onClick={sendDetails} disabled={busy === "send" || !m.email} data-testid="admin-send-login"
            className="btn-glow inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
            title={m.email ? "Regenerate temp password and email login details" : "Member has no email on file"}>
            <Mail size={12} /> Send login details
          </button>
        </div>

        {tempPw && (
          <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] p-4" data-testid="admin-temp-pw-panel">
            <div className="overline text-emerald-300 text-[10px]">Temporary password (shown once)</div>
            <div className="mt-2 flex items-center gap-3">
              <code className="flex-1 font-mono text-lg text-emerald-200 tracking-wide select-all">{tempPw}</code>
              <button onClick={copyTemp} className="btn-ghost rounded-full px-3 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1">
                <Copy size={11} /> Copy
              </button>
            </div>
            <div className="mt-2 text-[11px] text-white/50">Share via a secure channel. The member will be forced to change it on next login.</div>
          </div>
        )}
      </div>
    </div>
  );
}
